import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { CONSENT_ROUTE, isConsentGateExempt } from '../../config/consentGate';
import { useRole } from '../../contexts/AuthContext';

interface RoleProtectedRouteProps {
  /** The component/page to render if access is granted */
  children: ReactElement;
  /** Array of role names required to access this route */
  requiredRoles: string[];
  /** Path to redirect to if access is denied (default: '/unauthorized') */
  redirectTo?: string;
}

/**
 * Route guard that checks if the authenticated user has one of the required roles
 * 
 * @example
 * ```tsx
 * <Route path="/admin/users" element={
 *   <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
 *     <AdminUsers />
 *   </RoleProtectedRoute>
 * } />
 * ```
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRoles,
  redirectTo = '/unauthorized',
}) => {
  const { isLoading, isAuthenticated, hasActiveRole, requiresConsent } = useAuth();
  const { hasAnyRole } = useRole();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasActiveRole) {
    return <Navigate to="/select-role" state={{ from: location }} replace />;
  }

  // Mandatory consent gate (Terms + Privacy Policy), see ProtectedRoute.
  if (requiresConsent && !isConsentGateExempt(location.pathname)) {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to={CONSENT_ROUTE} replace />;
  }

  // Check if user has at least one of the required roles
  if (!hasAnyRole(requiredRoles)) {
    // User is authenticated but doesn't have required roles
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User is authenticated and has required roles
  return children;
};
